// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@gamesb42/ui-kit';

import Button from 'components/uiKit/Button';
import baseRequest from 'queries/baseRequest';
import endpoints from 'consts/endpoints';
import { formatAmount, formatDate, getCurrencyWithCode } from 'helpers/formatters';
import { AmountTransactionsType, ItemTransactionsType } from 'hooks/api/useTransaction';
import {
  capitalizeFirstLetter,
  getSymbolAndColorTransactions,
} from 'components/forms/ActivationOrderForm/Transactions/helpers';

import styles from './styles.module.scss';

type PropsT = {
  purchaseRequestId: string;
};

const Transactions: FC<PropsT> = ({ purchaseRequestId }) => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<ItemTransactionsType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadError, setIsLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const getTransactions = async () => {
      setIsLoading(true);
      setIsLoadError(false);

      try {
        const filter = [{ property: 'document_id', operator: '=', value: purchaseRequestId }];

        const result = await baseRequest({
          path: endpoints.getListTransactionsUrl({ page: 1, size: 100, filter: JSON.stringify(filter) }),
          options: { method: 'GET' },
        });

        if (isMounted) setTransactions(result?.items || []);
      } catch (error) {
        console.error('Ошибка при получении транзакций Purchase Request:', error);
        if (isMounted) setIsLoadError(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    if (purchaseRequestId) {
      getTransactions();
    } else {
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [purchaseRequestId, retryCount]);

  const columns = [
    {
      title: t('forms.purchaseRequest.transactionTitle'),
      dataIndex: 'value_datetime',
      key: 'value_datetime',
      render: (date: string) => formatDate(date),
    },
    {
      title: t('forms.purchaseRequest.transactionType'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => capitalizeFirstLetter(type),
    },
    {
      title: t('forms.purchaseRequest.transactionDetails'),
      dataIndex: 'details',
      key: 'details',
    },
    {
      title: t('forms.purchaseRequest.transactionAmount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as 'right',
      render: (amount: AmountTransactionsType, record: ItemTransactionsType) => {
        const { symbol, color } = getSymbolAndColorTransactions(amount?.amount, record.type);

        return (
          <span style={{ color }}>
            {`${symbol} ${getCurrencyWithCode(Number(amount?.currency))}${formatAmount(
              Math.abs(amount?.amount || 0),
            )}`}
          </span>
        );
      },
    },
  ];

  if (isLoadError) {
    return (
      <div className={styles.error}>
        <div>{t('forms.purchaseRequest.transactionsLoadError')}</div>
        <Button onClick={() => setRetryCount((count) => count + 1)}>
          {t('forms.purchaseRequest.transactionsRetry')}
        </Button>
      </div>
    );
  }

  if (!isLoading && transactions.length === 0) {
    return <div className={styles.empty}>{t('forms.purchaseRequest.transactionsEmpty')}</div>;
  }

  return <Table columns={columns} dataSource={transactions} loading={isLoading} rowKey="id" />;
};

export default Transactions;
